// The Brain stage — the dominant object on Brain Home.
//
// The artwork is a fixed asset and is never redrawn, traced or reinterpreted
// (IMPLEMENTATION.md rule 1). Everything this component adds is either a real
// department anchor or a state read from `brainState.js`. It invents nothing:
// there are exactly eleven department nodes plus the executive core, they sit
// on the anchors the package supplies, and their colour comes from live data.
//
// Layer order is the package's, top of README:
//   1  brain-master.png
//   2  activity canvas, clipped by brain-alpha-mask.png
//   3  brain-core-glow.png
//   4  hotspots and labels
// with brain-depth-glow.png underneath — an available asset, not in that list,
// so it is rendered first and is safe to drop (see BRAIN_ASSETS.md).
import React, { useCallback, useEffect, useRef, useState } from "react";

import BrainActivityCanvas from "./BrainActivityCanvas";
import DepartmentDetail from "./DepartmentDetail";
import { STATES } from "./brainState";
import "./brain-stage.css";

/** Normalized anchors, verbatim from `cipher-brain.hotspots.json`. Bound to
 *  Cipher's canonical department ids — verified against roster.py: all eleven
 *  present, none missing, none extra, `executive` separate from the set. */
export const CORE_ANCHOR = { id: "executive", x: 0.533, y: 0.493 };
export const HOTSPOT_ANCHORS = [
  { id: "analytics", x: 0.224, y: 0.398 },
  { id: "offer_performance", x: 0.36, y: 0.252 },
  { id: "creative_intelligence", x: 0.501, y: 0.187 },
  { id: "creative_production", x: 0.647, y: 0.252 },
  { id: "campaign_operations", x: 0.791, y: 0.38 },
  { id: "offer_discovery", x: 0.809, y: 0.551 },
  { id: "testing", x: 0.711, y: 0.693 },
  { id: "operator_development", x: 0.566, y: 0.775 },
  { id: "compliance", x: 0.435, y: 0.7 },
  { id: "engineering", x: 0.306, y: 0.612 },
  { id: "maintenance", x: 0.181, y: 0.526 },
];

const ASSET = (name) => `${process.env.PUBLIC_URL || ""}/brain/${name}`;

/** What a node says on hover. Counts only — never a judgment the data does
 *  not support. A department with no live agents says so rather than being
 *  described as "idle", which would imply it could be running and is not. */
function nodeMeta(dept) {
  if (!dept) return "";
  if (dept.state === STATES.CRITICAL) return "critical";
  if (dept.state === STATES.ATTENTION) return dept.suspended > 0 ? `${dept.suspended} suspended` : "needs attention";
  if (dept.state === STATES.REASONING) return "reasoning";
  if (dept.state === STATES.ACTIVE) return dept.runs === 1 ? "1 run" : `${dept.runs} runs`;
  return `${dept.live} of ${dept.total} live`;
}

export default function BrainStage({ brain, onOpenStream }) {
  const [selected, setSelected] = useState(null);
  const stageRef = useRef(null);
  const byId = {};
  for (const d of (brain && brain.departments) || []) byId[d.id] = d;

  const close = useCallback(() => setSelected(null), []);

  // Escape closes contextual detail and returns to the calm whole-Brain view.
  useEffect(() => {
    if (!selected) return undefined;
    const onKey = (e) => { if (e.key === "Escape") close(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [selected, close]);

  // A click on empty stage does the same. Clicks on a node stop propagation.
  const onStageClick = useCallback((e) => {
    if (e.target === e.currentTarget || e.target.classList.contains("cb-brain")) close();
  }, [close]);

  const overall = (brain && brain.overall) || STATES.HEALTHY;
  const totals = (brain && brain.totals) || { departments: 0, agents: 0, live: 0, currentRuns: 0 };

  const node = (anchor, isCore) => {
    const dept = isCore ? null : byId[anchor.id];
    const label = isCore ? "Executive core" : (dept ? dept.label : anchor.id);
    const state = isCore ? overall : (dept ? dept.state : STATES.HEALTHY);
    const meta = isCore ? "reasoning & decisions" : nodeMeta(dept);
    const open = selected === anchor.id;
    return (
      <button
        key={anchor.id}
        type="button"
        className={"cb-hot" + (isCore ? " cb-is-core" : "")}
        style={{ left: `${anchor.x * 100}%`, top: `${anchor.y * 100}%` }}
        data-id={anchor.id}
        data-state={state}
        data-testid={`cb-hot-${anchor.id}`}
        aria-expanded={open}
        aria-label={isCore
          ? "Executive core. Open the current reasoning and decision stream."
          : `${label}. ${meta}. Open department detail.`}
        onClick={(e) => {
          e.stopPropagation();
          if (isCore) { if (onOpenStream) onOpenStream(); return; }
          setSelected(open ? null : anchor.id);
        }}
      >
        <span className="cb-halo" />
        <span className="cb-node" />
        <span className="cb-tip">
          <span className="cb-tip-name">{label}</span>
          <span className="cb-tip-meta">{meta}</span>
        </span>
      </button>
    );
  };

  return (
    <div
      className="cb-stage"
      ref={stageRef}
      data-state={overall}
      data-testid="cb-stage"
      onClick={onStageClick}
    >
      <div className="cb-brain">
        {/* Not in the README's layer order — an available asset. Decorative
            depth only; dropping it changes no information. */}
        <img className="cb-layer cb-depth" src={ASSET("brain-depth-glow.png")} alt="" aria-hidden="true" />

        <img
          className="cb-layer cb-master"
          src={ASSET("brain-master.png")}
          alt={`Cipher's organisation drawn as a brain: ${totals.departments} departments, ` +
               `${totals.agents} agents, currently ${overall}.`}
        />

        {/* Travelling pulses only. Clipped by the alpha mask so nothing leaks
            outside the anatomy. Draws nothing without a real event. */}
        <BrainActivityCanvas
          brain={brain}
          anchors={HOTSPOT_ANCHORS}
          core={CORE_ANCHOR}
          maskSrc={ASSET("brain-alpha-mask.png")}
        />

        <img className="cb-layer cb-core" src={ASSET("brain-core-glow.png")} alt="" aria-hidden="true" />

        {HOTSPOT_ANCHORS.map((a) => node(a, false))}
        {node(CORE_ANCHOR, true)}
      </div>

      <div className="cb-foot">
        <span><i className="cb-foot-dot" /><b>{totals.departments}</b> DEPARTMENTS</span>
        <span><b>{totals.agents}</b> AGENTS</span>
        <span><b>{totals.live}</b> LIVE</span>
        <span>{totals.currentRuns === 0 ? "NO ACTIVE RUNS"
          : `${totals.currentRuns} ACTIVE RUN${totals.currentRuns === 1 ? "" : "S"}`}</span>
        <span>{String(overall).toUpperCase()}</span>
      </div>

      {selected && byId[selected] ? (
        <DepartmentDetail department={byId[selected]} onClose={close} />
      ) : null}
    </div>
  );
}
